import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../../config/api';
import { MapPin, Clock, Camera, User, CheckCircle2, Loader2, Calendar, ChevronDown, ChevronUp } from 'lucide-react';

const ClientTracking = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedVisit, setExpandedVisit] = useState(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const loadData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/worker/tracking/data?token=${token}`);
      if (!res.ok) {
        setError('Invalid or expired tracking link');
        setLoading(false);
        return;
      }
      const d = await res.json();
      setData(d);
    } catch {
      setError('Connection error');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (d) => {
    return new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-[#1a1f4e]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center" data-testid="tracking-error">
        <p className="text-red-600 font-medium">{error}</p>
        <p className="text-gray-500 text-sm mt-2">Please contact Just Cleaned for a new link.</p>
      </div>
    </div>
  );

  if (!data) return null;

  const { sites, today_visits, worker_locations, day_plans, photos, history } = data;

  // Group history by date
  const historyByDate = {};
  (history || []).forEach(v => {
    if (!historyByDate[v.date]) historyByDate[v.date] = [];
    historyByDate[v.date].push(v);
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a1f4e] text-white px-4 py-4">
        <div className="max-w-2xl mx-auto">
          <p className="text-sm text-white/60">Just Cleaned</p>
          <h1 className="text-xl font-bold">Cleaning Tracker</h1>
          <p className="text-sm text-white/70 mt-1">{sites.length} site{sites.length !== 1 ? 's' : ''} monitored</p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-6">
        {/* Today's Activity */}
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-3">Today's Activity</h2>
          {today_visits.length === 0 ? (
            <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
              <p className="text-gray-500">No cleaning visits scheduled for today</p>
            </div>
          ) : today_visits.map(visit => {
            const workerLoc = worker_locations[visit.worker_id];
            const visitPhotos = (photos || []).filter(p => p.visit_id === visit.id);
            const isExpanded = expandedVisit === visit.id;

            return (
              <div key={visit.id} className="bg-white rounded-xl border border-gray-200 p-4 mb-3" data-testid={`visit-card-${visit.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">{visit.site_name}</p>
                    <p className="text-sm text-gray-500">{visit.site_address}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    visit.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                  }`}>
                    {visit.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </div>

                <div className="flex items-center gap-3 text-sm text-gray-600 mb-2">
                  <span className="flex items-center gap-1"><User className="w-4 h-4" /> {visit.worker_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {formatTime(visit.started_at)}</span>
                  {visit.finished_at && <span>- {formatTime(visit.finished_at)}</span>}
                </div>

                {visit.status === 'in_progress' && workerLoc && (
                  <div className="bg-blue-50 rounded-lg p-3 mb-2">
                    <div className="flex items-center gap-2 text-sm text-blue-800">
                      <MapPin className="w-4 h-4" />
                      <span>Worker en route / on site</span>
                      <span className="text-xs text-blue-500 ml-auto">Updated {formatTime(workerLoc.timestamp)}</span>
                    </div>
                  </div>
                )}

                {/* Worker route info from day plan */}
                {day_plans.map(plan => {
                  const planSites = plan.sites || [];
                  const siteIdx = planSites.findIndex(s => s.site_id === visit.site_id);
                  if (siteIdx < 0) return null;
                  return (
                    <div key={plan.id} className="bg-gray-50 rounded-lg p-3 mb-2 text-sm text-gray-600">
                      <p className="font-medium text-gray-700 mb-1">Worker's route today:</p>
                      {planSites.map((s, i) => (
                        <span key={i} className={`inline-block mr-1 ${
                          s.site_id === visit.site_id ? 'font-bold text-[#1a1f4e]' : ''
                        }`}>
                          {i > 0 && ' → '}{s.site_name} (#{i + 1})
                        </span>
                      ))}
                    </div>
                  );
                })}

                {visitPhotos.length > 0 && (
                  <button
                    onClick={() => setExpandedVisit(isExpanded ? null : visit.id)}
                    className="flex items-center gap-2 text-sm text-[#1a1f4e] hover:underline mt-2"
                    data-testid={`visit-photos-toggle-${visit.id}`}
                  >
                    <Camera className="w-4 h-4" />
                    {visitPhotos.length} photo{visitPhotos.length !== 1 ? 's' : ''}
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                )}

                {isExpanded && visitPhotos.length > 0 && (
                  <div className="mt-3 space-y-3">
                    {['before', 'after'].map(type => {
                      const typePhotos = visitPhotos.filter(p => p.photo_type === type);
                      if (!typePhotos.length) return null;
                      return (
                        <div key={type}>
                          <p className="text-xs font-medium text-gray-500 uppercase mb-2">{type} photos</p>
                          <div className="grid grid-cols-3 gap-2">
                            {typePhotos.map(p => (
                              <img
                                key={p.id}
                                src={`${API_URL}/api/worker/photos/${p.storage_path}?auth=${token}`}
                                alt={type}
                                className="aspect-square object-cover rounded-lg"
                                loading="lazy"
                              />
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* Visit History */}
        {Object.keys(historyByDate).length > 0 && (
          <section>
            <h2 className="text-lg font-bold text-gray-900 mb-3">Visit History</h2>
            {Object.entries(historyByDate).map(([date, dateVisits]) => (
              <div key={date} className="mb-4">
                <p className="text-sm font-medium text-gray-500 mb-2 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(date)}
                </p>
                {dateVisits.map(v => (
                  <div key={v.id} className="bg-white rounded-lg border border-gray-200 p-3 mb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm text-gray-900">{v.site_name}</p>
                        <p className="text-xs text-gray-500">{v.worker_name} &middot; {formatTime(v.started_at)} - {formatTime(v.finished_at)}</p>
                      </div>
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default ClientTracking;
